import {
  answersFitSideBySide,
  type CodingAnswerOption,
} from '@seen/core/codings'
import {
  type InstrumentAnswers,
  instrumentAnswerKey,
} from '@seen/core/observation-logic/instruments/records'
import { instrumentAnswerOptions } from '@seen/core/terminology/instruments/registry'
import type { InstrumentItem } from '@seen/core/terminology/instruments/types'
import { cn } from '@seen/ui'
import { Fragment, type JSX, type KeyboardEvent, useId, useRef } from 'react'
import { ChoiceSelect } from '#src/components/rich-text-editor/ObservationNode/panel/ChoiceSelect.tsx'
import {
  LABEL_CELL,
  LABEL_COLUMN,
} from '#src/components/rich-text-editor/ObservationNode/panel/instrumentItems.ts'

// A row's cells share one stripe: under the pointer, or holding keyboard
// focus. A click leaves no stripe behind once the pointer moves on.
const ROW_CELL =
  'px-0.5 py-0.5 group-hover/row:bg-muted/60 group-has-[:focus-visible]/row:bg-muted/60'

export interface LastAnswer {
  option: CodingAnswerOption
  // When it was recorded, as shown: "Mar 2", "today".
  day: string | null
  // Where it is from: "from PHQ-2, this note", "recorded 2026-03-02".
  source: string
}

export interface ChoiceMatrixProps {
  // Items sharing one answer list, one row each.
  items: readonly InstrumentItem[]
  answers: InstrumentAnswers
  // An earlier answer, marked on its own cell while the row is unanswered,
  // by answer key.
  last?: Readonly<Record<string, LastAnswer>>
  // `null` clears the row.
  onAnswer: (item: InstrumentItem, option: CodingAnswerOption | null) => void
  disabled?: boolean
  // One question whose answers are its own: they wrap in the answer column
  // instead of lining up in columns with other rows, or, when too many or
  // too long to sit side by side, sit behind a searchable select.
  wrap?: boolean
}

function chosenCode(
  answers: InstrumentAnswers | undefined,
  item: InstrumentItem
): string | undefined {
  const value = answers?.[instrumentAnswerKey(item.coding)]
  return value?.kind === 'coding' ? value.value[0]?.code : undefined
}

// A Likert grid: a row per item, each answer in its own column. One tab
// stop per row. In a row: a number key answers with the answer of that score
// (or position) and moves to the next row; ←/→ step the answer; ↑/↓ move
// between rows; Backspace or Delete clears it, as does clicking the chosen
// answer again.
export function ChoiceMatrix({
  items,
  answers,
  last,
  onAnswer,
  disabled = false,
  wrap = false,
}: ChoiceMatrixProps): JSX.Element {
  const options = items[0] ? instrumentAnswerOptions(items[0]) : []
  const select =
    wrap &&
    !answersFitSideBySide(
      options.map((option) => option.display ?? option.code)
    )
  const rows = useRef<(HTMLDivElement | null)[]>([])
  const hintId = useId()

  const focusCell = (row: number, column?: number): void => {
    const cells =
      rows.current[row]?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []
    const checked = [...cells].findIndex(
      (cell) => cell.getAttribute('aria-checked') === 'true'
    )
    cells[column ?? Math.max(checked, 0)]?.focus()
  }

  const onKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    item: InstrumentItem,
    index: number
  ): void => {
    if (disabled) {
      return
    }
    const chosen = options.findIndex(
      (option) => option.code === chosenCode(answers, item)
    )
    const handled = (): void => {
      event.preventDefault()
      event.stopPropagation()
    }
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        const step = event.key === 'ArrowRight' ? 1 : -1
        const next =
          chosen < 0
            ? step > 0
              ? 0
              : options.length - 1
            : Math.min(Math.max(chosen + step, 0), options.length - 1)
        const option = options[next]
        handled()
        if (option && next !== chosen) {
          onAnswer(item, option)
        }
        focusCell(index, next)
        return
      }
      case 'ArrowUp':
      case 'ArrowDown': {
        handled()
        const row = index + (event.key === 'ArrowDown' ? 1 : -1)
        if (row >= 0 && row < items.length) {
          focusCell(row)
        }
        return
      }
      case 'Backspace':
      case 'Delete':
        handled()
        if (chosen >= 0) {
          onAnswer(item, null)
        }
        return
    }
    if (!/^\d$/.test(event.key)) {
      return
    }
    const digit = Number(event.key)
    const option =
      options.find((candidate) => candidate.score === digit) ??
      (options.every((candidate) => candidate.score === undefined)
        ? options[digit]
        : undefined)
    if (!option) {
      return
    }
    handled()
    onAnswer(item, option)
    focusCell(Math.min(index + 1, items.length - 1))
  }

  // Every cell reads its answer in full; the score only drives the digit keys.
  return (
    // The trailing flexible track keeps the answer columns at their own
    // width and carries the row stripe to the edge; short of room, the
    // answer columns narrow and their text wraps.
    <div
      className="grid text-sm"
      style={{
        gridTemplateColumns: wrap
          ? `${LABEL_COLUMN} minmax(0, 1fr)`
          : `${LABEL_COLUMN} repeat(${options.length}, auto) minmax(0, 1fr)`,
      }}
      data-choice-matrix={wrap ? undefined : ''}
      data-choice-single={wrap ? '' : undefined}
    >
      {items.map((item, index) => {
        const chosen = chosenCode(answers, item)
        const offer = chosen
          ? undefined
          : last?.[instrumentAnswerKey(item.coding)]
        const renderOption = (option: CodingAnswerOption): JSX.Element => {
          const picked = chosen === option.code
          const priorHint =
            offer?.option.code === option.code
              ? `Last answer${offer.day ? ` · ${offer.day}` : ''} (${offer.source})`
              : undefined
          const prior = priorHint !== undefined
          return (
            <Fragment key={option.code}>
              {/* biome-ignore lint/a11y/useSemanticElements: grid cells read better as buttons than native radios */}
              <button
                type="button"
                role="radio"
                aria-checked={picked}
                aria-label={`${item.label}: ${option.display}`}
                aria-describedby={prior ? `${hintId}-${index}` : undefined}
                title={priorHint}
                data-prior-answer={prior ? '' : undefined}
                disabled={disabled}
                tabIndex={picked || (!chosen && option === options[0]) ? 0 : -1}
                onClick={() => onAnswer(item, picked ? null : option)}
                className={cn(
                  'm-0.5 inline-flex min-h-6 items-center gap-1 rounded-md border border-gray-450 px-1.5 py-0.5 text-left text-xs leading-4 hover:bg-gray-460 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 disabled:cursor-not-allowed disabled:opacity-60',
                  picked &&
                    'border-primary bg-primary text-primary-foreground hover:bg-primary',
                  // Dotted, unlike the solid focus ring and the filled
                  // answer, so it never reads as either.
                  prior &&
                    'outline outline-1 outline-offset-2 outline-dashed outline-muted-foreground/40'
                )}
              >
                {option.display ?? option.code}
              </button>
              {prior && (
                <span id={`${hintId}-${index}`} className="sr-only">
                  {priorHint}
                </span>
              )}
            </Fragment>
          )
        }
        if (select) {
          return (
            <div
              key={instrumentAnswerKey(item.coding)}
              role="group"
              aria-label={item.label}
              className="group/row contents"
            >
              <span className={cn(ROW_CELL, LABEL_CELL)}>{item.label}</span>
              <span
                className={cn(ROW_CELL, 'flex items-start')}
                data-answer-cell=""
              >
                <ChoiceSelect
                  label={item.label}
                  options={options}
                  chosen={chosen}
                  offer={offer}
                  disabled={disabled}
                  onAnswer={(option) => onAnswer(item, option)}
                />
              </span>
            </div>
          )
        }
        return (
          <div
            key={instrumentAnswerKey(item.coding)}
            ref={(element) => {
              rows.current[index] = element
            }}
            role="radiogroup"
            aria-label={item.label}
            onKeyDown={(event) => onKeyDown(event, item, index)}
            // The row has no box of its own, so each cell paints the stripe
            // that follows the pointer (or focus) across the question.
            className="group/row contents"
          >
            <span className={cn(ROW_CELL, LABEL_CELL)}>{item.label}</span>
            {wrap ? (
              <span
                className={cn(ROW_CELL, 'flex flex-wrap items-start')}
                data-answer-cell=""
              >
                {options.map(renderOption)}
              </span>
            ) : (
              <>
                {options.map((option) => (
                  <span
                    key={option.code}
                    className={cn(ROW_CELL, 'flex items-start')}
                    data-answer-cell=""
                  >
                    {renderOption(option)}
                  </span>
                ))}
                <span aria-hidden className={ROW_CELL} />
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
